'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, ArrowRight, Check, Sparkles, Building2, HardHat, Package, Zap, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type LoginForm = { email: string; password: string; remember: boolean };
type RegisterForm = { name: string; email: string; phone: string; password: string; confirmPassword: string; role: string; agreeTerms: boolean };

const demoCredentials = [
  { role: 'Owner/Pemilik', email: 'budi.santoso@gmail.com', password: 'JuraganRenov2026!', icon: <Building2 size={13} />, color: '#1A56DB', bg: 'rgba(26,86,219,0.1)' },
  { role: 'Kontraktor', email: 'ahmad.fauzi@konstruksi.id', password: 'Kontrak2026!', icon: <HardHat size={13} />, color: '#E8A020', bg: 'rgba(232,160,32,0.1)' },
  { role: 'Arsitek', email: 'dewi.arsitek@studio.id', password: 'Desain2026!', icon: <Sparkles size={13} />, color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
  { role: 'Supplier', email: 'sari.supplier@material.id', password: 'Vendor2026!', icon: <Package size={13} />, color: '#D4006A', bg: 'rgba(212,0,106,0.1)' },
];

const roles = [
  { id: 'role-owner', value: 'owner', label: 'Owner / Pemilik', desc: 'Renovasi atau bangun properti', icon: <Building2 size={16} /> },
  { id: 'role-contractor', value: 'contractor', label: 'Kontraktor', desc: 'Jasa konstruksi', icon: <HardHat size={16} /> },
  { id: 'role-architect', value: 'architect', label: 'Arsitek / Desainer', desc: 'Jasa desain', icon: <Sparkles size={16} /> },
  { id: 'role-supplier', value: 'supplier', label: 'Supplier / Vendor', desc: 'Material bangunan', icon: <Package size={16} /> },
];

// Google Icon SVG
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5836-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574C.3477 6.1732 0 7.5482 0 9s.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
      <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1632 6.656 3.5795 9 3.5795z" fill="#EA4335"/>
    </svg>
  );
}

export default function SignUpLoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const router = useRouter();
  const { signIn, signUp, signInWithGoogle } = useAuth();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const registerForm = useForm<RegisterForm>({ defaultValues: { name: '', email: '', phone: '', password: '', confirmPassword: '', role: 'owner', agreeTerms: false } });

  const handleCredentialFill = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.success(`Kredensial ${cred.role} diisi otomatis`);
  };

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Selamat datang kembali!');
      router.push('/owner-dashboard');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || 'Login gagal';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
        loginForm.setError('email', { message: 'Email atau password tidak valid' });
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onRegister = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Password tidak cocok' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name, role: data.role });
      toast.success('Akun berhasil dibuat! Silakan masuk.');
      setMode('login');
      loginForm.setValue('email', data.email);
    } catch (error: any) {
      const msg = error?.message || 'Pendaftaran gagal';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        registerForm.setError('email', { message: 'Email sudah terdaftar, silakan masuk' });
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Redirect handled by OAuth callback
    } catch (error: any) {
      toast.error(error?.message || 'Login dengan Google gagal');
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)' }}>
      {/* Left brand panel — dark navy */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0D1B2A 0%, #0F2744 50%, #1A0A2E 100%)' }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 grid-lines" />

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(26,86,219,0.18) 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,0,106,0.12) 0%, transparent 70%)' }} />

        <div className="relative">
          <div className="flex items-center gap-2.5 mb-14">
            <AppLogo src="/assets/images/logo_juragan_renov-1787058203675.jpeg" size={36} />
            <div>
              <span className="text-white font-extrabold text-lg block">Juragan Renov</span>
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#E8A020', fontSize: '0.6rem' }}>2.0 · AI-Powered</span>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-5" style={{ background: 'rgba(26,86,219,0.15)', border: '1px solid rgba(26,86,219,0.3)', color: '#60A5FA' }}>
            <Zap size={11} fill="currentColor" />
            Platform Renovasi & Konstruksi
          </div>

          <h2 className="text-3xl font-extrabold text-white mb-4 leading-tight tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            Kelola Proyek Anda
            <br />
            <span style={{ background: 'linear-gradient(90deg, #E8A020 0%, #F5C842 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Lebih Cerdas
            </span>
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Kelola seluruh siklus proyek konstruksi Anda dari satu platform. Transparan, terverifikasi, dan terdokumentasi.
          </p>
        </div>

        {/* Feature list */}
        <div className="relative space-y-3">
          {[
            { icon: <Zap size={13} />, text: 'Estimasi RAB berbasis AI dalam 5 menit', color: '#60A5FA' },
            { icon: <ShieldCheck size={13} />, text: '2.400+ profesional terverifikasi', color: '#34D399' },
            { icon: <Check size={13} />, text: 'Milestone payment yang aman & transparan', color: '#E8A020' },
          ].map((item, i) => (
            <div key={`brand-point-${i}`} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.08)', color: item.color }}>
                {item.icon}
              </div>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{item.text}</span>
            </div>
          ))}

          {/* Brand color palette */}
          <div className="flex items-center gap-2 pt-4">
            {['#1A56DB', '#D4006A', '#E8A020', '#0E8A4A'].map((c) => (
              <div key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />
            ))}
            <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Juragan Renov 2.0</span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <AppLogo src="/assets/images/logo_juragan_renov-1787058203675.jpeg" size={32} />
            <div>
              <span className="font-extrabold text-sm block" style={{ color: 'var(--foreground)' }}>Juragan Renov</span>
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#E8A020', fontSize: '0.6rem' }}>2.0</span>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-xl p-1 mb-8" style={{ background: 'var(--muted)' }}>
            {(['login', 'register'] as const).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === m ? 'shadow-card' : ''}`}
                style={mode === m ? { background: 'var(--card)', color: 'var(--foreground)' } : { color: 'var(--muted-foreground)' }}
              >
                {m === 'login' ? 'Masuk' : 'Daftar'}
              </button>
            ))}
          </div>

          {/* Google Sign In Button — shown for both login and register */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border font-semibold text-sm transition-all duration-200 hover:shadow-md mb-4"
            style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--foreground)' }}
          >
            {isGoogleLoading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <GoogleIcon />
            )}
            {isGoogleLoading ? 'Menghubungkan...' : (mode === 'login' ? 'Masuk dengan Google' : 'Daftar dengan Google')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>atau dengan email</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <div className="mb-6">
                <h1 className="text-2xl font-extrabold mb-1 tracking-tight" style={{ color: 'var(--foreground)', letterSpacing: '-0.02em' }}>Selamat Datang Kembali</h1>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Masuk ke akun JuraganRenov Anda</p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Email</label>
                <input
                  type="email"
                  className={`input-field ${loginForm.formState.errors.email ? 'input-error' : ''}`}
                  placeholder="nama@email.com"
                  {...loginForm.register('email', { required: 'Email wajib diisi', pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: 'Format email tidak valid' } })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`input-field pr-10 ${loginForm.formState.errors.password ? 'input-error' : ''}`}
                    placeholder="Password Anda"
                    {...loginForm.register('password', { required: 'Password wajib diisi', minLength: { value: 6, message: 'Minimal 6 karakter' } })}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded" {...loginForm.register('remember')} />
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Ingat saya</span>
                </label>
                <Link href="#" className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Lupa password?</Link>
              </div>

              <button type="submit" className="btn-primary w-full py-3 rounded-xl" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Memproses...
                  </span>
                ) : (
                  <>Masuk ke Dashboard <ArrowRight size={16} /></>
                )}
              </button>

              {/* Demo credentials */}
              <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: 'var(--border)', background: 'var(--background)' }}>
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: 'var(--muted-foreground)' }}>Akun Demo — Klik untuk mengisi</p>
                <div className="space-y-2">
                  {demoCredentials.map((cred) => (
                    <button
                      key={`demo-${cred.role}`}
                      type="button"
                      onClick={() => handleCredentialFill(cred)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition-all duration-200 hover:shadow-sm"
                      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cred.bg, color: cred.color }}>
                          {cred.icon}
                        </div>
                        <div>
                          <p className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>{cred.role}</p>
                          <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>{cred.email}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold" style={{ color: cred.color }}>Gunakan</span>
                    </button>
                  ))}
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
              <div className="mb-6">
                <h1 className="text-2xl font-extrabold mb-1 tracking-tight" style={{ color: 'var(--foreground)', letterSpacing: '-0.02em' }}>Buat Akun Baru</h1>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Bergabung dengan ribuan pemilik properti di Indonesia</p>
              </div>

              {/* Role selection */}
              <div className="space-y-2">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Saya adalah</label>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map((role) => {
                    const selected = registerForm.watch('role') === role.value;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => registerForm.setValue('role', role.value)}
                        className="flex flex-col items-start p-3 rounded-xl border text-left transition-all duration-200"
                        style={{
                          borderColor: selected ? 'var(--primary)' : 'var(--border)',
                          background: selected ? 'var(--primary-light)' : 'var(--card)',
                          boxShadow: selected ? '0 0 0 1px var(--primary)' : 'none',
                        }}
                      >
                        <span style={{ color: selected ? 'var(--primary)' : 'var(--muted-foreground)' }}>{role.icon}</span>
                        <p className="text-xs font-bold mt-1.5" style={{ color: selected ? 'var(--primary)' : 'var(--foreground)' }}>{role.label}</p>
                        <p className="text-xs mt-0.5" style={{ color: selected ? 'var(--primary)' : 'var(--muted-foreground)', opacity: 0.8 }}>{role.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Nama Lengkap</label>
                <input type="text" className={`input-field ${registerForm.formState.errors.name ? 'input-error' : ''}`} placeholder="Nama lengkap Anda"
                  {...registerForm.register('name', { required: 'Nama wajib diisi', minLength: { value: 2, message: 'Minimal 2 karakter' } })} />
                {registerForm.formState.errors.name && <p className="text-xs" style={{ color: 'var(--danger)' }}>{registerForm.formState.errors.name.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Email</label>
                  <input type="email" className={`input-field ${registerForm.formState.errors.email ? 'input-error' : ''}`} placeholder="nama@email.com"
                    {...registerForm.register('email', { required: 'Email wajib diisi' })} />
                  {registerForm.formState.errors.email && <p className="text-xs" style={{ color: 'var(--danger)' }}>{registerForm.formState.errors.email.message}</p>}
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>No. Telepon</label>
                  <input type="tel" className="input-field" placeholder="08xx-xxxx-xxxx"
                    {...registerForm.register('phone', { required: 'No. telepon wajib diisi' })} />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} className="input-field pr-10" placeholder="Min. 8 karakter"
                    {...registerForm.register('password', { required: 'Password wajib diisi', minLength: { value: 8, message: 'Minimal 8 karakter' } })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Konfirmasi Password</label>
                <div className="relative">
                  <input type={showConfirmPassword ? 'text' : 'password'} className={`input-field pr-10 ${registerForm.formState.errors.confirmPassword ? 'input-error' : ''}`} placeholder="Ulangi password"
                    {...registerForm.register('confirmPassword', { required: 'Konfirmasi password wajib diisi' })} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword && <p className="text-xs" style={{ color: 'var(--danger)' }}>{registerForm.formState.errors.confirmPassword.message}</p>}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5 rounded" {...registerForm.register('agreeTerms', { required: 'Anda harus menyetujui syarat & ketentuan' })} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Saya setuju dengan{' '}
                  <Link href="#" className="font-semibold" style={{ color: 'var(--primary)' }}>Syarat & Ketentuan</Link>{' '}
                  dan{' '}
                  <Link href="#" className="font-semibold" style={{ color: 'var(--primary)' }}>Kebijakan Privasi</Link>{' '}
                  JuraganRenov
                </span>
              </label>
              {registerForm.formState.errors.agreeTerms && <p className="text-xs" style={{ color: 'var(--danger)' }}>{registerForm.formState.errors.agreeTerms.message}</p>}

              <button type="submit" className="btn-primary w-full py-3 rounded-xl" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Membuat akun...
                  </span>
                ) : (
                  <>Buat Akun <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}